import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, InputAdornment, TextField } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import SelecaoPerfis, { TOpenModalPerfilData } from "./SelecaoPerfis";
import { TOpenModalUsuario, TPerfilValue } from "..";
import { IDataDetalheUsuario, IDetalheUsuarioPerfil } from "../../../../types/api/configuracoes/detalhar-usuario";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosClient from "../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { ICadUsuarioInput, TCadUsuarioPerfilInput } from "../../../../types/mutations/cadastrar-usuario";
import { IAttUsuarioInput } from "../../../../types/mutations/atualizar-usuario";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

export default function DialogCriacaoEditUser({
  open,
  defaultValue,
  handleClose,
}: DialogCriacaoEditUserProps) {
  const queryClient = useQueryClient();

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [form, setForm] = useState<TFormPerfil>({
    id: null,
    nome: "",
    email: "",
    senha: "",
    perfis: [],
  });

  const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue]);
  const valoresIniciais = useMemo(() => (defaultValue?.defaultValue ? defaultValue.defaultValue : null), [defaultValue]);
  const podeContinuar = useMemo(() => {
    return form.nome === "" || form.email === "" || form.perfis?.length <= 0;
  }, [form]);

  //queries e mutations
  const { data, isFetching } = useQuery({
    queryKey: ["detalhar-usuario/{id}", isEdit],
    queryFn: () => {
      if (isEdit) {
        return axiosClient.get(`/detalhar-usuario/${valoresIniciais?.id}`);
      }
      return null;
    },
    cacheTime: 0,
  });
  const { dataDetalheUsuario }: IDetalheUsuario = useMemo(
    () => ({
      dataDetalheUsuario: data?.data,
    }),
    [data]
  );

  const mapValueToPerfil = (value: TPerfilValue): IDetalheUsuarioPerfil => ({
    id: value.id,
    nome: value.nome,
    parametros_obrigatorios: value.parametros_obrigatorios ?? [],
    loja: value.loja ?? null,
    vendedor: value.vendedor ?? null,
    condicoes_pagamento_blocked: value.condicoes_pagamento_blocked ?? [],
    limite_alcada: value.limite_alcada ?? null,
    tipo_publico: value.tipo_publico ?? [],
  });

  const { mutate: criarUsuario, isLoading: isLoadingCriarUsuario } =
    useMutation({
      mutationKey: "cadastrar-usuario",
      mutationFn: (inputs: ICadUsuarioInput) =>
        axiosClient.post("/cadastrar-usuario", inputs),
      onError: (error) => {
        console.error(error);
        enqueueSnackbar("Erro ao cadastrar usuário!", { variant: "error" });
      },
      onSuccess: () => {
        queryClient.invalidateQueries("pesquisar-usuarios");
        handleClose();
        enqueueSnackbar("Cadastro do usuário realizado com sucesso!", {
          variant: "success",
        });
      },
    });

  const { mutate: attUsuario, isLoading: isLoadingAttUsuario } = useMutation({
    mutationKey: "atualizar-usuario",
    mutationFn: (inputs: IAttUsuarioInput) =>
      axiosClient.put(`/atualizar-usuario/${inputs.id}`, inputs),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao atualizar usuário!", { variant: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries("pesquisar-usuarios");
      queryClient.invalidateQueries(`detalhar-usuario/{id}`);
      handleClose();
      enqueueSnackbar("Atualização do usuário realizada com sucesso!", {
        variant: "success",
      });
    },
  });
  //

  //funções manipuladoras
  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleChangeText = useCallback(
    (prop: "nome" | "email" | "senha", value: string) => {
      setForm((prevState) => ({ ...prevState, [prop]: value }));
    },
    []
  );

  const handleAddPerfis = useCallback(
    (value: TPerfilValue) => {
      const converted = mapValueToPerfil(value);
      const index = form.perfis.findIndex(
        (_, indexItem) => indexItem === value.index
      );

      if (index !== -1) {
        const newForm = [...form.perfis];
        newForm[index] = converted;
        setForm((prevState) => ({ ...prevState, perfis: newForm }));
      } else {
        setForm((prevState) => ({
          ...prevState,
          perfis: [...prevState.perfis, converted],
        }));
      }
    },
    [form.perfis]
  );

  const handleDeletePerfis = useCallback(
    (value: TOpenModalPerfilData) => {
      const updatedForm = form.perfis.filter((perfil) => {
        const samePerfil = perfil.id === value?.id;
        const sameLoja = perfil.loja?.id === value?.loja?.id;
        return !(samePerfil && sameLoja);
      });
      setForm((prevState) => ({ ...prevState, perfis: updatedForm }));
    },
    [form.perfis]
  );

  const handleSubmit = useCallback(() => {
    const params = {
      nome: form.nome,
      email: form.email,
      perfis: form.perfis.map((item) => {
        let perfil: TCadUsuarioPerfilInput = {
          perfil_id: item.id,
        };
        if (item.loja) perfil.loja_id = item.loja.id;
        if (item.vendedor) perfil.vendedor_id = item.vendedor.id;
        if (item.condicoes_pagamento_blocked)
          perfil.condicoes_pagamento_blocked_ids =
            item.condicoes_pagamento_blocked.map((cond) => Number(cond.id));
        if (item.limite_alcada) perfil.limite_alcada = item.limite_alcada;
        if (item.tipo_publico)
          perfil.tipo_publico =
            item.tipo_publico.map((publico) => publico);
        return perfil;
      }),
    };

    if (isEdit) attUsuario({ ...params, id: Number(valoresIniciais?.id) });
    if (!isEdit) criarUsuario({ ...params, senha: form.senha });
  }, [form, isEdit, criarUsuario, attUsuario, valoresIniciais]);
  //

  useEffect(() => {
    if (dataDetalheUsuario && isEdit) {
      setForm({
        id: dataDetalheUsuario.id,
        nome: dataDetalheUsuario.nome,
        email: dataDetalheUsuario.email,
        perfis: dataDetalheUsuario.perfis,
        senha: "",
      });
    }
  }, [dataDetalheUsuario, isEdit]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{`${!isEdit ? "Adicionar" : "Editar"} usuário`}</DialogTitle>
      {isFetching && (
        <Box display="flex" justifyContent="center" mt={4} mb={8}>
          <CircularProgress />
        </Box>
      )}

      {!isFetching && (
        <>
          <DialogContent>
            <Grid container spacing={1}>
              <Grid size={{ xs: isEdit ? 6 : 12 }}>
                <TextField
                  fullWidth
                  id="outlined-basic"
                  label="Nome"
                  variant="outlined"
                  margin="normal"
                  value={form.nome}
                  onChange={(e) => handleChangeText("nome", e.target.value)}
                />
              </Grid>

              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  id="outlined-basic"
                  label="Email"
                  variant="outlined"
                  margin="normal"
                  value={form.email}
                  onChange={(e) => handleChangeText("email", e.target.value)}
                />
              </Grid>

              {!isEdit && (
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    id="outlined-basic"
                    label="Senha"
                    variant="outlined"
                    margin="normal"
                    type={showPassword ? "text" : "password"}
                    value={form.senha}
                    onChange={(e) => handleChangeText("senha", e.target.value)}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleClickShowPassword}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }
                    }}
                  />
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <SelecaoPerfis
                  perfis={form.perfis}
                  onSubmit={(valor) => handleAddPerfis(valor)}
                  onDelete={(valor) => handleDeletePerfis(valor)}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose}>Cancelar</Button>
            <Button
              variant="contained"
              autoFocus
              disabled={
                podeContinuar || isLoadingCriarUsuario || isLoadingAttUsuario
              }
              onClick={handleSubmit}
            >
              {(isLoadingCriarUsuario || isLoadingAttUsuario) && (
                <CircularProgress size={20} sx={{ marginRight: 1 }} />
              )}
              Confirmar
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

interface IDetalheUsuario {
  dataDetalheUsuario: IDataDetalheUsuario;
}
interface DialogCriacaoEditUserProps {
  open: boolean;
  defaultValue: TOpenModalUsuario;
  handleClose: () => void;
}

type TFormPerfil = {
  id: number | null;
  senha: string;
  nome: string;
  email: string;
  perfis: IDetalheUsuarioPerfil[];
};
